import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  setForcedChoiceQuestions,
  preloadForcedChoiceAnswers,
  selectForcedChoiceAnswer,
  resetQuizProgress,
  goToNextForcedChoiceQuestion,
  goToPreviousForcedChoiceQuestion,
  goToSpecificForcedChoiceQuestion,
  addCompletedCategory,
  setActiveCategory,
  setTraitAssessmentResults,
  resetForcedChoiceQuiz,
} from '../../redux/slices/forcedChoiceQuizSlice';
import { addAssessment as addCoachAssessment } from '../../redux/slices/coachSlice';
import { addAssessment as addAthleteAssessment } from '../../redux/slices/athleteSlice';
import { useNavigate } from 'react-router-dom';
import ToastPopup from '../../components/ToastPopup/ToastPopup';
import BackButton from '../../components/BackButton/BackButton';

import useForcedChoiceQuestions from '../../hooks/quiz/useForcedChoiceQuestions';
import useFetchTraitAssessmentProgress from '../../hooks/quiz/useFetchTraitAssessmentProgress';
import useTraitAssessmentProgress from '../../hooks/quiz/useTraitAssessmentProgress';
import useSubmitTraitAssessment from '../../hooks/quiz/useSubmitTraitAssessment';

import QuestionNavigationPanel from '../../components/QuestionNavigationPanel/QuestionNavigationPanel';
import QuizQuestion from '../../components/QuizQuestion/QuizQuestion';
import { TRAIT_CATEGORIES } from '../../utils/traitCategories';
import { motion } from 'framer-motion';

import styles from '../QuizDashboard/QuizDashboard.module.css';

const ForcedChoiceQuizDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [toast, setToast] = useState({ message: '', type: 'alert', isVisible: false });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showToast = (message, type = 'alert') => {
    setToast({ message, type, isVisible: true });
    setTimeout(() => {
      setToast(prev => ({ ...prev, isVisible: false }));
    }, 4000);
  };

  const {
    activeCategory,
    questionsBySection,
    currentSection,
    currentQuestionIndex,
    answers,
    isSubmitted,
  } = useSelector(state => state.forcedChoiceQuiz);

  const userRole = useSelector(state => state.auth.role);

  const currentSectionQuestions = questionsBySection[currentSection] || [];
  const currentQuestion = currentSectionQuestions[currentQuestionIndex];

  const { fetchQuestions, loading } = useForcedChoiceQuestions();
  const { fetchProgress, fetchAllTraitProgress } = useFetchTraitAssessmentProgress();
  const { saveProgress } = useTraitAssessmentProgress();
  const { submitAssessment } = useSubmitTraitAssessment();

  useEffect(() => {
    if (activeCategory && !questionsBySection.length) {
      fetchQuestions({ variables: { category: activeCategory } });
    }
  }, [activeCategory, questionsBySection.length, fetchQuestions]);

  useEffect(() => {
    if (!activeCategory) {
      navigate('/dashboard');
      return;
    }

    if (!questionsBySection.length) {
      fetchQuestions({ variables: { category: activeCategory } });
    }
  }, [activeCategory, questionsBySection.length, fetchQuestions, navigate]);

  useEffect(() => {
    const init = async () => {
      if (!activeCategory) {
        navigate('/dashboard');
        return;
      }

      if (!questionsBySection.length) {
        const { data: qData } = await fetchQuestions({ variables: { category: activeCategory } });

        const chunked = Array.from({ length: Math.ceil(qData.getForcedChoiceQuestions.length / 30) }, (_, i) =>
          qData.getForcedChoiceQuestions.slice(i * 30, (i + 1) * 30)
        );

        dispatch(setForcedChoiceQuestions(chunked));
      }

      const { data: progressData } = await fetchProgress({ variables: { category: activeCategory } });
      if (progressData?.getTraitAssessmentProgress?.answers) {
        dispatch(preloadForcedChoiceAnswers(progressData.getTraitAssessmentProgress.answers));
      }
    };

    init();
  }, [activeCategory, fetchQuestions, fetchProgress, dispatch, navigate, questionsBySection.length]);

  const handleAnswerSelect = (selectedOption) => {
    dispatch(selectForcedChoiceAnswer({
      questionId: currentQuestion._id,
      selectedOption,
    }));
  };

  const handleNext = () => dispatch(goToNextForcedChoiceQuestion());
  const handlePrevious = () => dispatch(goToPreviousForcedChoiceQuestion());

  const handleSubmitSection = async () => {
    const sectionAnswers = currentSectionQuestions.map((q) => {
      const selected = answers.find(a => a.questionId === q._id);
      return {
        questionId: q._id,
        selectedOption: selected?.selectedOption || '',
      };
    });

    const unanswered = sectionAnswers.some(a => !a.selectedOption || a.selectedOption.trim() === '');
    if (unanswered) {
      showToast("Please answer all questions before submitting this section.", "alert");
      return;
    }

    setIsSubmitting(true);

    try {
      await saveProgress({
        variables: {
          category: activeCategory,
          answers: sectionAnswers,
          isCompleted: true,
        },
      });

      const justCompleted = activeCategory;
      const currentIndex = TRAIT_CATEGORIES.indexOf(justCompleted);
      const nextCategory = TRAIT_CATEGORIES[currentIndex + 1];

      dispatch(addCompletedCategory(justCompleted));

      if (nextCategory) {
        dispatch(resetQuizProgress());
        dispatch(setActiveCategory(nextCategory));
        navigate('/dashboard/trait-quiz');
      } else {
        try {
          const { data } = await fetchAllTraitProgress();
          const fullAnswers = data?.getAllTraitAssessmentProgress?.answers || [];

          const submitRes = await submitAssessment({
            variables: { input: { answers: fullAnswers } },
          });

          if (submitRes?.data?.submitTraitAssessment) {
            const result = submitRes.data.submitTraitAssessment;

            dispatch(setTraitAssessmentResults(result));

            const assessment = {
              _id: result._id,
              type: 'trait-assessment',
              dateTaken: result.dateTaken,
              profile: result.profile,
              confidence: result.confidence,
            };

            if (userRole === 'athlete') {
              dispatch(addAthleteAssessment(assessment));
            } else {
              dispatch(addCoachAssessment(assessment));
            }

          }

          dispatch(resetForcedChoiceQuiz());
          navigate('/dashboard');
        } catch (err) {
          console.error('❌ Error submitting final assessment:', err.message);
          showToast("An error occurred while submitting. Please try again.", "error");
        }
      }
    } catch (err) {
      console.error('❌ Error saving progress:', err.message);
      showToast("Failed to save section progress. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.dashboardWrapper}>
      {loading && <p>Loading questions...</p>}
      {!loading && currentQuestion && (
        <>
          <h2 className={styles.sectionHeader}>
            Section {TRAIT_CATEGORIES.indexOf(activeCategory) + 1} of 8: {activeCategory}
          </h2>
          <BackButton to="/dashboard" />
          <QuestionNavigationPanel
            questions={currentSectionQuestions}
            selectedAnswers={answers}
            currentIndex={currentQuestionIndex}
            onQuestionSelect={(i) => dispatch(goToSpecificForcedChoiceQuestion(i))}
            isAnsweredFn={(q) => answers.some(a => a.questionId === q._id)}
          />
          <QuizQuestion
            question={currentQuestion}
            currentIndex={currentQuestionIndex}
            totalQuestions={currentSectionQuestions.length}
            onAnswerSelect={handleAnswerSelect}
            selectedAnswer={answers.find(a => a.questionId === currentQuestion._id)?.selectedOption}
            isSubmitted={isSubmitted}
          />
          <div className={styles.navigationButtons}>
            <button onClick={handlePrevious} disabled={currentQuestionIndex === 0}>Previous</button>
            {currentQuestionIndex < currentSectionQuestions.length - 1 && (
              <button onClick={handleNext}>Next</button>
            )}
            {currentQuestionIndex === currentSectionQuestions.length - 1 && !isSubmitted && (
              isSubmitting ? (
                <button disabled style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                    style={{
                      width: '1rem',
                      height: '1rem',
                      border: '2px solid var(--color-border)',
                      borderTop: '2px solid var(--color-highlight)',
                      borderRadius: '50%',
                    }}
                  />
                  Submitting...
                </button>
              ) : (
                <button onClick={handleSubmitSection}>Submit Section</button>
              )
            )}
          </div>
        </>
      )}
      <ToastPopup
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
      />
    </div>
  );
};

export default ForcedChoiceQuizDashboard;
